import React, { createContext, useContext, useEffect, useState } from "react";

import { apiCall } from "api/axios";
import { API_ERROR_MESSAGES } from "api/axios/types";
import {
  IBusinessDetails,
  IBusinessDetailsResponseData,
} from "api/business-user-management/types";
import {
  IBusinessMetricsData,
  IBusinessMetricsResponse,
  IBusinessesResponse,
} from "api/business/types";
import {
  IComplianceTrail,
  IComplianceTrailsResponse,
  IKYBVerificationResultsData,
  IKYBVerificationResultsResponse,
  TBusinessRegistrationType,
} from "api/compliance/types";
import { fetchData, useFetchData } from "hooks/useFetchData";
import { ROUTES } from "routes/constants";
import { splitStringAtPosition } from "utils";
import { ZERO } from "utils/constants";

import { customErrorToast } from "ui/custom-toast";

import { BusinessComplianceQueryKeys } from "./consts";
import {
  IBusinessComplianceContextProps,
  IBusinessComplianceFilterPayload,
  IBusinessComplianceProviderProps,
} from "./types";

const INDEX_OFFSET = 1;
const BUSINESS_REG_NO_SPLIT_POS = 2;
const UI_LOADING_TIMEOUT = 100;
export const BUSINESS_LIST_LIMIT = 15;
const DEFAULT_METRICS_DATA: IBusinessMetricsData = {
  totalBusinesses: ZERO,
  activeBusinesses: ZERO,
  inActiveBusinesses: ZERO,
  pendingBusinesses: ZERO,
  inReviewBusinesses: ZERO,
  rejectedBusinesses: ZERO,
};

const BusinessComplianceContext =
  createContext<IBusinessComplianceContextProps>({
    businessesResult: null,
    setBusinessesResult: () => {
      return null;
    },
    handleFetchBusinesses: async () => {
      return null;
    },
    isFetchingBusinesses: false,
    isFetchingMoreBusinesses: false,
    handleFetchBusinessDetails: async () => {
      return null;
    },
    isLoadingBusinessDetails: false,
    setIsLoadingBusinessDetails: () => {
      return null;
    },
    selectedBusiness: null,
    setSelectedBusiness: () => {
      return null;
    },
    handleGetComplianceTrails: async () => {
      return null;
    },
    isLoadingComplianceTrails: false,
    complianceTrails: [],
    setComplianceTrails: () => {
      return null;
    },
    handleGetKybVerificationResults: async () => {
      return null;
    },
    businessComplianceUILoading: false,
    kybVerificationResultsData: null,
    isLoadingKybVerificationResults: false,
    businessRegistrationType: "RC",
    businessMetricsData: DEFAULT_METRICS_DATA,
    setBusinessMetricsData: () => {
      // default
    },
  });

export const extractKeywordFromPathName = (path: string): string => {
  const segments = path.split("/");
  const lastSegment = segments[segments.length - INDEX_OFFSET];
  const parts = lastSegment.split("-");
  return parts[parts.length - INDEX_OFFSET];
};

export const BusinessComplianceProvider = (
  props: IBusinessComplianceProviderProps
) => {
  const { children } = props;

  const [isFetchingBusinesses, setIsFetchingBusinesses] = useState(true);
  const [isFetchingMoreBusinesses, setIsFetchingMoreBusinesses] =
    useState(false);
  const [isLoadingBusinessDetails, setIsLoadingBusinessDetails] =
    useState(true);
  const [selectedBusiness, setSelectedBusiness] =
    useState<IBusinessDetails | null>(null);
  const [isLoadingComplianceTrails, setIsLoadingComplianceTrails] =
    useState(false);
  const [isLoadingKybVerificationResults, setIsLoadingKybVerificationResults] =
    useState(false);
  const [kybVerificationResultsData, setKybVerificationResultsData] =
    useState<IKYBVerificationResultsData | null>(null);
  const [businessRegistrationType, setBusinessRegistrationType] =
    useState<TBusinessRegistrationType>("RC");

  const [businessComplianceUILoading, setBusinessComplianceUILoading] =
    useState(false);
  const [complianceTrails, setComplianceTrails] = useState<IComplianceTrail[]>(
    []
  );
  const [businessesResult, setBusinessesResult] =
    useState<IBusinessesResponse | null>(null);

  const [businessMetricsData, setBusinessMetricsData] =
    useState<IBusinessMetricsData>(DEFAULT_METRICS_DATA);
  const businessMetricsResult = useFetchData<IBusinessMetricsResponse>(
    [BusinessComplianceQueryKeys.BUSINESS_METRICS],
    {
      apiRoute: "/admin/metrics/businesses",
      module: "EARNIPAY_STG",
      useEncryption: false,
    },
    {
      enabled() {
        const shouldEnable = window.location.pathname.includes(
          ROUTES.BUSINESSES_COMPLIANCE_PAGE
        );
        return shouldEnable;
      },
    }
  );

  const handleFetchBusinesses = async (payload: {
    page: number;
    status: string;
    isFetchingMore?: boolean;
    searchQuery?: string;
    filterPayload?: IBusinessComplianceFilterPayload;
    limit?: number;
  }): Promise<IBusinessesResponse | null> => {
    const { page, status, isFetchingMore, searchQuery, filterPayload, limit } =
      payload;
    if (isFetchingMore) {
      setIsFetchingMoreBusinesses(true);
    } else {
      setIsFetchingBusinesses(true);
    }
    let queryString = "";
    if (filterPayload) {
      queryString = `&${Object.entries(filterPayload)
        .filter(([_, value]) => value !== undefined && value !== null)
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        )
        .join("&")}`;
    }
    let response = null;
    let apiRoute = `/admin/search-filter-employers?page=${page}&limit=${limit || BUSINESS_LIST_LIMIT}&status=${status}&download=false${queryString}`;

    if (searchQuery) {
      apiRoute += `&keyword=${searchQuery}`;
    }
    try {
      response = await fetchData({
        apiRoute,
        module: "EARNIPAY_STG",
        useEncryption: false,
      });
    } catch (error) {
      console.error("Error: ", error);
    }
    if (isFetchingMore) {
      setIsFetchingMoreBusinesses(false);
    } else {
      setIsFetchingBusinesses(false);
    }
    return response as unknown as IBusinessesResponse;
  };

  const handleFetchBusinessDetails = async (
    businessId: string
  ): Promise<IBusinessDetailsResponseData | null> => {
    setIsLoadingBusinessDetails(true);
    setSelectedBusiness(null);
    setKybVerificationResultsData(null);
    let response;
    let result = null;

    try {
      response = await apiCall({
        apiRoute: "/control/business/compliance",
        module: "EARNIPAY_API",
        config: {
          method: "POST",
          data: {
            businessId,
          },
        },
        useEncryption: true,
      });
      result = response as unknown as IBusinessDetailsResponseData;
      if (result?.error) {
        customErrorToast({ message: API_ERROR_MESSAGES.NETWORK_ERROR });
      }
      setSelectedBusiness(result?.data ?? null);
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsLoadingBusinessDetails(false);
    return result;
  };

  const handleGetComplianceTrails = async (
    businessId: string
  ): Promise<IComplianceTrailsResponse | null> => {
    setIsLoadingComplianceTrails(true);
    setComplianceTrails([]);
    let response;
    let result = null;

    try {
      response = await fetchData({
        apiRoute: `/admin/compliance-trails/${businessId}`,
        module: "EARNIPAY_STG",
        useEncryption: false,
      });
      result = response as unknown as IComplianceTrailsResponse;
      if (!result) {
        customErrorToast({ message: API_ERROR_MESSAGES.NETWORK_ERROR });
      }
      setComplianceTrails(result?.complianceTrails ?? []);
    } catch (error) {
      console.error("Error: ", error);
      const err: any = error;
      if (err?.error) {
        customErrorToast({ message: err?.message });
      }
    }
    setIsLoadingComplianceTrails(false);
    return result;
  };

  const handleGetKybVerificationResults = async (
    businessRegistrationNum: string
  ): Promise<IKYBVerificationResultsResponse | null> => {
    const [businessType, businessRegNumber] = splitStringAtPosition(
      businessRegistrationNum,
      BUSINESS_REG_NO_SPLIT_POS
    );

    const businessRegType =
      businessType.toUpperCase() as TBusinessRegistrationType;
    setBusinessRegistrationType(businessRegType);
    setIsLoadingKybVerificationResults(true);
    setKybVerificationResultsData(null);
    let response;
    let result = null;

    try {
      response = await fetchData({
        apiRoute: `/miscellaneous/search-kyb?type=${businessRegType}&searchTerm=${businessRegNumber}`,
        module: "EARNIPAY_STG",
        useEncryption: false,
      });

      result = response as unknown as IKYBVerificationResultsResponse;
      if (!result) {
        customErrorToast({ message: API_ERROR_MESSAGES.NETWORK_ERROR });
      }
      setKybVerificationResultsData(result?.data ?? null);
    } catch (error) {
      console.error("Error: ", error);
    }
    setIsLoadingKybVerificationResults(false);
    return result;
  };

  useEffect(() => {
    if (isFetchingBusinesses || isLoadingBusinessDetails) {
      setBusinessComplianceUILoading(true);
    }

    let timer: NodeJS.Timeout;
    if (!isFetchingBusinesses && !isLoadingBusinessDetails) {
      timer = setTimeout(() => {
        setBusinessComplianceUILoading(false);
      }, UI_LOADING_TIMEOUT);
    }

    return () => clearTimeout(timer);
  }, [isFetchingBusinesses, isLoadingBusinessDetails]);

  useEffect(() => {
    if (
      businessMetricsResult?.data?.data &&
      businessMetricsData.totalBusinesses === ZERO
    ) {
      setBusinessMetricsData(businessMetricsResult?.data?.data);
    }
  }, [businessMetricsResult, businessMetricsData]);

  return (
    <BusinessComplianceContext.Provider
      value={{
        businessMetricsData,
        setBusinessMetricsData,
        businessesResult,
        setBusinessesResult,
        handleFetchBusinesses,
        isFetchingBusinesses,
        isFetchingMoreBusinesses,
        handleFetchBusinessDetails,
        isLoadingBusinessDetails,
        setIsLoadingBusinessDetails,
        selectedBusiness,
        setSelectedBusiness,
        handleGetComplianceTrails,
        isLoadingComplianceTrails,
        complianceTrails,
        setComplianceTrails,
        handleGetKybVerificationResults,
        businessComplianceUILoading,
        kybVerificationResultsData,
        isLoadingKybVerificationResults,
        businessRegistrationType,
      }}
    >
      {children}
    </BusinessComplianceContext.Provider>
  );
};

export const useBusinessCompliance = () =>
  useContext(BusinessComplianceContext);
